import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc, query, where } from 'firebase/firestore';
import { auth, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';

interface Question {
    id: string;
    Question: string;
    Answer01: string;
    Answer02: string;
    Answer03: string;
    CollectAnswer: string;
    Chapter: number;
    Qno: number;
    SKref: any; // Reference
    SKrefName?: string;
}

interface Shikaku {
    id: string;
    ShikakuName: string;
}

interface SyncResult {
    success: boolean;
    results: {
        created: number;
        updated: number;
        errors: number;
        errorDetails: string[];
    };
}

export default function QuestionsManagement() {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [shikakuList, setShikakuList] = useState<Shikaku[]>([]);
    const [selectedShikaku, setSelectedShikaku] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);

    // Form state
    const [form, setForm] = useState<Partial<Question>>({
        Question: '',
        Answer01: '',
        Answer02: '',
        Answer03: '',
        CollectAnswer: '',
        Chapter: 1,
        Qno: 1,
        SKref: null
    });
    const [selectedShikakuForForm, setSelectedShikakuForForm] = useState<string>('');

    useEffect(() => {
        fetchShikaku();
    }, []);

    useEffect(() => {
        if (selectedShikaku) {
            fetchQuestions(selectedShikaku);
        } else {
            setQuestions([]);
            setSelectedQuestionIds([]);
            setLoading(false);
        }
    }, [selectedShikaku]);

    const fetchShikaku = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'Shikaku'));
            const shikakuData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ShikakuName: doc.data().ShikakuName || ''
            })) as Shikaku[];
            setShikakuList(shikakuData);
            if (shikakuData.length > 0) {
                setSelectedShikaku(shikakuData[0].id);
            }
        } catch (error) {
            console.error('Error fetching Shikaku:', error);
        }
    };

    const fetchQuestions = async (shikakuId: string) => {
        setLoading(true);
        try {
            // Create a reference to the Shikaku document
            const shikakuRef = doc(db, 'Shikaku', shikakuId);

            // Query questions where SKref equals the Shikaku reference
            const q = query(collection(db, 'Questions'), where('SKref', '==', shikakuRef));
            const querySnapshot = await getDocs(q);

            const questionsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Question[];

            // Sort by Chapter then Qno
            questionsData.sort((a, b) => {
                if (a.Chapter !== b.Chapter) return a.Chapter - b.Chapter;
                return a.Qno - b.Qno;
            });

            setQuestions(questionsData);
            setSelectedQuestionIds([]);
        } catch (error) {
            console.error('Error fetching questions:', error);
            alert('問題の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (question: Question) => {
        setEditingQuestion(question);
        setForm({ ...question });
        // Set the selected shikaku for the form based on the question's SKref
        if (question.SKref && question.SKref.id) {
            setSelectedShikakuForForm(question.SKref.id);
        } else if (selectedShikaku) {
            setSelectedShikakuForForm(selectedShikaku);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('この問題を削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'Questions', id));
            alert('問題を削除しました');
            fetchQuestions(selectedShikaku);
        } catch (error: any) {
            console.error('Error deleting question:', error);
            const currentUser = auth.currentUser;
            const debugInfo = `
Error Code: ${error.code}
Error Message: ${error.message}
User ID: ${currentUser?.uid}
Email: ${currentUser?.email}
Time: ${new Date().toLocaleString()}
            `;
            alert(`削除に失敗しました:\n${debugInfo}`);
        }
    };

    const handleSave = async () => {
        if (!form.Question || !form.Answer01 || !form.CollectAnswer || !selectedShikakuForForm) {
            alert('必須項目を入力してください');
            return;
        }

        try {
            const shikakuRef = doc(db, 'Shikaku', selectedShikakuForForm);
            const saveData = {
                ...form,
                SKref: shikakuRef,
                Chapter: Number(form.Chapter),
                Qno: Number(form.Qno)
            };

            if (editingQuestion) {
                await updateDoc(doc(db, 'Questions', editingQuestion.id), saveData);
                alert('問題を更新しました');
            } else {
                await addDoc(collection(db, 'Questions'), saveData);
                alert('問題を追加しました');
            }

            setEditingQuestion(null);
            setIsAdding(false);
            setForm({
                Question: '',
                Answer01: '',
                Answer02: '',
                Answer03: '',
                CollectAnswer: '',
                Chapter: 1,
                Qno: 1
            });
            fetchQuestions(selectedShikaku);
        } catch (error: any) {
            console.error('Error saving question:', error);
            const currentUser = auth.currentUser;
            const debugInfo = `
Error Code: ${error.code}
Error Message: ${error.message}
User ID: ${currentUser?.uid}
Email: ${currentUser?.email}
Time: ${new Date().toLocaleString()}
            `;
            alert(`保存に失敗しました:\n${debugInfo}`);
        }
    };

    const openAddModal = () => {
        setIsAdding(true);
        setForm({
            Question: '',
            Answer01: '',
            Answer02: '',
            Answer03: '',
            CollectAnswer: '',
            Chapter: 1,
            Qno: 1
        });
        setSelectedShikakuForForm(selectedShikaku);
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleDeleteAll = async () => {
        if (selectedQuestionIds.length === 0) {
            alert('削除する問題を選択してください');
            return;
        }

        const confirmMsg = `【警告】\n選択した問題 (${selectedQuestionIds.length}件) を削除します。\n\nこの操作は取り消せません。\n本当に削除しますか?`;

        if (!confirm(confirmMsg)) return;

        // 二重確認
        if (!confirm('本当によろしいですか? この操作は取り消せません。')) return;

        setLoading(true);
        try {
            let deletedCount = 0;
            for (const id of selectedQuestionIds) {
                await deleteDoc(doc(db, 'Questions', id));
                deletedCount++;
            }
            alert(`${deletedCount}件の問題を削除しました`);

            // Refresh questions list
            if (selectedShikaku) {
                fetchQuestions(selectedShikaku);
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleToggleQuestion = (questionId: string) => {
        setSelectedQuestionIds((prev) => (
            prev.includes(questionId)
                ? prev.filter((id) => id !== questionId)
                : [...prev, questionId]
        ));
    };

    const allSelected = questions.length > 0 && selectedQuestionIds.length === questions.length;

    const handleToggleAllQuestions = () => {
        setSelectedQuestionIds(allSelected ? [] : questions.map((q) => q.id));
    };

    const handleSync = async () => {
        if (!confirm('スプレッドシートからデータを同期しますか?\n既存のデータは上書きされます。')) return;

        setIsSyncing(true);
        try {
            const syncFunction = httpsCallable(functions, 'syncSpreadsheetToFirestore');
            const result = await syncFunction();

            const data = result.data as SyncResult;
            setSyncResult(data);
            alert(`同期完了!\n新規作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`);

            // Refresh questions list
            if (selectedShikaku) {
                fetchQuestions(selectedShikaku);
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">問題管理</h1>
                        <p className="text-gray-600">資格試験の問題を管理します</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSync}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSyncing ? (
                                <>
                                    <span className="animate-spin">⟳</span>
                                    同期中...
                                </>
                            ) : (
                                <>
                                    📊 スプレッドシートから同期
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleDeleteAll}
                            disabled={loading || selectedQuestionIds.length === 0}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            🗑️ 選択問題を削除
                        </button>
                        <button
                            onClick={openAddModal}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ 問題追加
                        </button>
                    </div>
                </div >

                {/* Filter */}
                < div className="bg-white p-4 rounded-xl shadow-sm mb-6 flex items-center gap-4" >
                    <label className="font-bold text-gray-700">資格を選択:</label>
                    <select
                        value={selectedShikaku}
                        onChange={(e) => setSelectedShikaku(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        {shikakuList.map(shikaku => (
                            <option key={shikaku.id} value={shikaku.id}>{shikaku.ShikakuName}</option>
                        ))}
                    </select>
                    <Link
                        to="/admin/questions/shikaku-list"
                        className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                    >
                        資格一覧
                    </Link>
                </div >

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    現在の表示件数: {questions.length} 件
                </div>
                <div className="mb-4 text-sm text-gray-600 font-medium">
                    選択中: {selectedQuestionIds.length} 件
                </div>

                {/* Questions List */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-500">読み込み中...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 w-14">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={handleToggleAllQuestions}
                                                aria-label="すべて選択"
                                            />
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-20">Chapter</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-20">No.</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">問題文</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-32">正解</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 w-72">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {questions.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                                問題が登録されていません
                                            </td>
                                        </tr>
                                    ) : (
                                        questions.map((q) => (
                                            <tr key={q.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedQuestionIds.includes(q.id)}
                                                        onChange={() => handleToggleQuestion(q.id)}
                                                        aria-label={`問題${q.id}を選択`}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-800 text-center">{q.Chapter}</td>
                                                <td className="px-6 py-4 text-sm text-gray-800 text-center">{q.Qno}</td>
                                                <td className="px-6 py-4 text-sm text-gray-800">
                                                    <div className="line-clamp-2">{q.Question}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-800">
                                                    <div className="line-clamp-1" title={q.CollectAnswer}>{q.CollectAnswer}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-xs text-gray-500 font-mono">UID: {q.id}</span>
                                                        <button
                                                            onClick={() => handleEdit(q)}
                                                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                                        >
                                                            編集
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(q.id)}
                                                            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                                        >
                                                            削除
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div >

            {/* Edit/Add Modal */}
            {/* Sync Result Modal */}
            {
                syncResult && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">同期結果</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">新規作成:</span>
                                    <span className="font-bold text-green-600">{syncResult.results.created}件</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">更新:</span>
                                    <span className="font-bold text-blue-600">{syncResult.results.updated}件</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">エラー:</span>
                                    <span className="font-bold text-red-600">{syncResult.results.errors}件</span>
                                </div>
                                {syncResult.results.errorDetails && syncResult.results.errorDetails.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700 mb-2">エラー詳細:</p>
                                        <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                                            {syncResult.results.errorDetails.map((error: string, index: number) => (
                                                <p key={index} className="text-sm text-red-700">{error}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setSyncResult(null)}
                                className="mt-6 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Edit/Add Modal */}
            {
                (editingQuestion || isAdding) && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {isAdding ? '問題追加' : '問題編集'}
                                </h2>
                            </div>

                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">資格</label>
                                        <select
                                            value={selectedShikakuForForm}
                                            onChange={(e) => setSelectedShikakuForForm(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">選択してください</option>
                                            {shikakuList.map(s => (
                                                <option key={s.id} value={s.id}>{s.ShikakuName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Chapter</label>
                                            <input
                                                type="number"
                                                value={form.Chapter}
                                                onChange={(e) => setForm({ ...form, Chapter: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">No.</label>
                                            <input
                                                type="number"
                                                value={form.Qno}
                                                onChange={(e) => setForm({ ...form, Qno: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">問題文</label>
                                    <textarea
                                        value={form.Question}
                                        onChange={(e) => setForm({ ...form, Question: e.target.value })}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">選択肢 1</label>
                                        <input
                                            type="text"
                                            value={form.Answer01}
                                            onChange={(e) => setForm({ ...form, Answer01: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">選択肢 2</label>
                                        <input
                                            type="text"
                                            value={form.Answer02}
                                            onChange={(e) => setForm({ ...form, Answer02: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">選択肢 3</label>
                                        <input
                                            type="text"
                                            value={form.Answer03}
                                            onChange={(e) => setForm({ ...form, Answer03: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">正解</label>
                                    <select
                                        value={form.CollectAnswer}
                                        onChange={(e) => setForm({ ...form, CollectAnswer: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">選択してください</option>
                                        {form.Answer01 && <option value={form.Answer01}>{form.Answer01}</option>}
                                        {form.Answer02 && <option value={form.Answer02}>{form.Answer02}</option>}
                                        {form.Answer03 && <option value={form.Answer03}>{form.Answer03}</option>}
                                    </select>
                                </div>
                            </div>

                            <div className="p-6 border-t flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setEditingQuestion(null);
                                        setIsAdding(false);
                                    }}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    保存
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}
